/* ===========================================================
   WISHLIST — storefront-styled saved products view.
   Modeled on compare-page.jsx for visual parity.
   =========================================================== */
import React from "react";
import { ProductTile, STOCK_PILL } from "@/components/product-card";
import { Breadcrumbs, toast } from "@/components/shell";
import { inr } from "@/data/data";
import { Icons } from "@/lib/icons";
import { useProductsBySlugs, useAddCartItem } from "@/api/hooks";
import { useWishlist } from "@/hooks/useWishlist";

function WishlistPage() {
  const wishlist = useWishlist();
  const slugs = wishlist.slugs;
  const queries = useProductsBySlugs(slugs);
  const addToCart = useAddCartItem();

  const products = queries.map((q) => q.data).filter(Boolean);
  const isLoading = queries.some((q) => q.isLoading);

  const handleMove = (slug, name) => {
    addToCart.mutate({ slug, qty: 1 }, {
      onSuccess: () => { wishlist.toggle(slug); toast && toast(`Moved “${name}” to cart`); },
      onError: (err) => toast && toast(err?.message || "Couldn't move to cart", "error"),
    });
  };

  const handleMoveAll = () => {
    products.forEach((p) => addToCart.mutate({ slug: p.slug, qty: 1 }));
    products.forEach((p) => wishlist.toggle(p.slug));
    toast && toast(`Moved ${products.length} items to cart`);
  };

  const handleClear = () => {
    if (!window.confirm("Remove all items from your wishlist?")) return;
    slugs.forEach((s) => wishlist.toggle(s));
    toast && toast("Wishlist cleared");
  };

  return (
    <>
      <Breadcrumbs items={[{ label: "Wishlist" }]} />
      <div className="container compare-page">
        <header style={{ padding: "32px 0 24px", display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div>
            <h1 style={{ fontSize: "clamp(28px, 3.6vw, 40px)", letterSpacing: "-0.025em" }}>Your wishlist</h1>
            <p style={{ color: "var(--ink-3)", fontSize: 15, marginTop: 8 }}>
              {slugs.length} {slugs.length === 1 ? "item" : "items"} saved · sync across devices when signed in.
            </p>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {products.length > 0 && (
              <>
                <button className="btn btn-outline btn-sm" onClick={handleClear}>
                  <Icons.X size={14} stroke={2} /> Clear all
                </button>
                <button className="btn btn-primary btn-sm" onClick={handleMoveAll} disabled={addToCart.isPending}>
                  <Icons.Cart size={14} stroke={2} /> Move all to cart
                </button>
              </>
            )}
            {products.length === 0 && (
              <a href="/pages/storefront/products.html" className="btn btn-outline btn-sm">
                Browse catalog <Icons.ArrowR size={14} stroke={2} />
              </a>
            )}
          </div>
        </header>

        {isLoading && slugs.length > 0 ? (
          <div className="empty"><div className="empty-title">Loading saved products…</div></div>
        ) : products.length === 0 ? (
          <div className="empty">
            <div className="empty-ic"><Icons.Heart size={26} stroke={1.6} /></div>
            <div className="empty-title">Your wishlist is empty</div>
            <div className="empty-desc">Tap the heart icon on any product card to save it here. We'll keep track of stock changes and price drops on saved items.</div>
            <div className="empty-actions">
              <a className="btn btn-primary" href="/pages/storefront/products.html">Browse catalog</a>
              <a className="btn btn-ghost" href="/pages/storefront/categories.html">Explore categories</a>
            </div>
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
              gap: 16,
              paddingBottom: 48,
            }}
          >
            {products.map((p) => {
              const stock = STOCK_PILL[p.stock_status] || STOCK_PILL.in_stock;
              return (
                <article
                  key={p.slug}
                  style={{
                    display: "flex", flexDirection: "column", gap: 12,
                    padding: 16, background: "var(--paper-2)",
                    border: "1px solid var(--line)", borderRadius: 12,
                    position: "relative",
                  }}
                >
                  <button
                    onClick={() => { wishlist.toggle(p.slug); toast && toast("Removed from wishlist"); }}
                    aria-label="Remove from wishlist"
                    style={{
                      position: "absolute", top: 10, right: 10, zIndex: 2,
                      width: 30, height: 30, borderRadius: 999,
                      background: "var(--paper)", border: "1px solid var(--line)",
                      display: "inline-flex", alignItems: "center", justifyContent: "center",
                      cursor: "pointer", color: "var(--ink-3)",
                    }}
                  >
                    <Icons.X size={13} stroke={2} />
                  </button>

                  <ProductTile product={p} size="md" />

                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                    <span className={"pill " + stock.cls}>{stock.label}</span>
                    {p.badge && <span style={{ fontSize: 11, color: "var(--ink-3)", fontFamily: "var(--font-mono)" }}>{p.badge}</span>}
                  </div>

                  <a
                    href={"/pages/storefront/product.html?slug=" + p.slug}
                    style={{ fontWeight: 500, fontSize: 14, color: "#0a0a0a", lineHeight: 1.4 }}
                  >
                    {p.name}
                  </a>

                  <div style={{ fontSize: 12, color: "var(--ink-3)" }}>
                    {p.brand} · {p.unit_label}
                  </div>

                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: "auto" }}>
                    <div style={{ fontWeight: 600, fontSize: 16, color: "#0a0a0a" }}>
                      {inr(p.price_min)}
                      {p.price_max !== p.price_min && (
                        <span style={{ fontSize: 12, color: "var(--ink-3)", marginLeft: 4 }}>–{inr(p.price_max)}</span>
                      )}
                    </div>
                    {p.rating && (
                      <span style={{ fontSize: 12, color: "var(--ink-3)" }}>
                        {p.rating.toFixed(1)} ★ · {(p.reviews ?? 0).toLocaleString("en-IN")}
                      </span>
                    )}
                  </div>

                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => handleMove(p.slug, p.name)}
                    disabled={addToCart.isPending || p.stock_status === "out_of_stock"}
                  >
                    <Icons.Cart size={13} stroke={2} />
                    {p.stock_status === "out_of_stock" ? "Notify me" : "Move to cart"}
                  </button>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

export { WishlistPage };
